/**
 * Maps the v4 React SDK login/register argument onto v5.
 *
 * v5 `generateAuthUrl` copies only a known camelCase allowlist onto
 * `/oauth2/auth`. A key that is not in that allowlist is preserved on
 * `state` when it still has to round-trip (the OAuth `state` blob), and
 * any key this mapper does not recognise throws — dropping one sends the
 * user to the wrong connection with no error.
 *
 * `post_login_redirect_url` has no v5 authorize-URL slot. It stays on
 * `state`. `app_state.redirectTo` becomes `state.redirectTo` only when
 * the call site already passed it.
 *
 * `firstName` and `lastName` are accepted and then discarded. Kinde has
 * no name parameter, and nothing reads the names back. Kinde echoes
 * OAuth `state` on the redirect URL (`/scribe?code=…&state=…`), and
 * session replay records that URL, so a name in `state` would land in
 * Datadog. The web `/api/auth/register` query is a different client and
 * still sends the names.
 *
 * `LoginOptions.properties` is not a general extra-params bag.
 * `@kinde/js-utils` `generateAuthUrl` copies a `properties` entry onto
 * `/oauth2/auth` only when the key is in its marketing allowlist
 * (`utm_*`, `gclid`, `fbclid`, …). Any other key is `console.warn`ed
 * and dropped. `post_login_redirect_url` is not on that list, so
 * `properties` would discard it. v4's pkce client appended every
 * `authUrlParams` key onto the authorize URL after `generateAuthUrl`;
 * v5 has no equivalent pass-through.
 */

import { logger } from '@/utils/logger/logger';

export type KindeV4AuthUrlParams = {
  connection_id?: string;
  login_hint?: string;
  lang?: string;
  org_code?: string;
  org_name?: string;
  is_create_org?: boolean;
  post_login_redirect_url?: string;
  firstName?: string;
  lastName?: string;
};

export type KindeV4ReactAuthArgs = {
  authUrlParams: KindeV4AuthUrlParams;
  app_state?: {
    redirectTo?: string;
  };
};

export type KindeReactLoginArgs = {
  connectionId?: string;
  loginHint?: string;
  lang?: string;
  orgCode?: string;
  orgName?: string;
  isCreateOrg?: boolean;
  state?: Record<string, string>;
};

const AUTHORIZE_STRING_KEYS = {
  connection_id: 'connectionId',
  login_hint: 'loginHint',
  lang: 'lang',
  org_code: 'orgCode',
  org_name: 'orgName',
} as const satisfies Record<string, keyof KindeReactLoginArgs>;

const STATE_STRING_KEYS = ['post_login_redirect_url'] as const;

// Names are not copied onto OAuth state. See the file comment.
const DROPPED_AUTH_URL_KEYS = ['firstName', 'lastName'] as const;

const KNOWN_AUTH_URL_KEYS = new Set<string>([
  ...Object.keys(AUTHORIZE_STRING_KEYS),
  ...STATE_STRING_KEYS,
  ...DROPPED_AUTH_URL_KEYS,
  'is_create_org',
]);

function requireString(key: string, value: unknown): string {
  if (typeof value !== 'string') {
    throw new Error(`Kinde auth param "${key}" must be a string`);
  }
  return value;
}

export function toKindeReactLoginArgs(args: KindeV4ReactAuthArgs): KindeReactLoginArgs {
  const params = args.authUrlParams;
  for (const key of Object.keys(params)) {
    if (!KNOWN_AUTH_URL_KEYS.has(key)) {
      throw new Error(`Kinde auth param "${key}" has no v5 equivalent`);
    }
  }

  const result: KindeReactLoginArgs = {};
  for (const [from, to] of Object.entries(AUTHORIZE_STRING_KEYS) as [
    keyof typeof AUTHORIZE_STRING_KEYS,
    (typeof AUTHORIZE_STRING_KEYS)[keyof typeof AUTHORIZE_STRING_KEYS],
  ][]) {
    const value = params[from];
    if (value === undefined) continue;
    result[to] = requireString(from, value);
  }

  if (params.is_create_org !== undefined) {
    if (typeof params.is_create_org !== 'boolean') {
      throw new Error('Kinde auth param "is_create_org" must be a boolean');
    }
    result.isCreateOrg = params.is_create_org;
  }

  const state: Record<string, string> = {};
  if (args.app_state?.redirectTo !== undefined) {
    state.redirectTo = requireString('redirectTo', args.app_state.redirectTo);
  }
  for (const key of STATE_STRING_KEYS) {
    const value = params[key];
    if (value === undefined) continue;
    state[key] = requireString(key, value);
  }
  if (Object.keys(state).length > 0) {
    result.state = state;
  }

  return result;
}

function kindeAuthParamClassification(error: unknown): string {
  if (!(error instanceof Error)) return 'kinde_auth_param_rejected';
  if (error.message.includes('has no v5 equivalent')) return 'unknown_kinde_auth_param';
  if (
    error.message.includes('must be a string') ||
    error.message.includes('must be a boolean')
  ) {
    return 'invalid_kinde_auth_param';
  }
  return 'kinde_auth_param_rejected';
}

/**
 * Maps login args for a click handler. A rejected param logs a
 * classification and returns null so the caller can leave loading unset.
 * The thrown message names the key and is not logged.
 */
export function tryKindeReactLoginArgs(
  args: KindeV4ReactAuthArgs
): KindeReactLoginArgs | null {
  try {
    return toKindeReactLoginArgs(args);
  } catch (error) {
    logger.warn('Kinde auth params rejected', {
      classification: kindeAuthParamClassification(error),
    });
    return null;
  }
}
